import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Newspaper } from 'lucide-react';

const News = () => {
  const newsItems = [
    {
      id: 1,
      slug: 'new-uk-gambling-regulations',
      title: 'New UK Gambling Regulations Take Effect: What Players Need to Know',
      excerpt: 'Recent changes to UK gambling laws introduce stricter affordability checks and enhanced player protection measures. Here\'s how these updates affect your casino experience.',
      date: '01.12.2025'
    },
    {
      id: 2,
      slug: 'top-5-slot-games-december',
      title: 'Top 5 Slot Games Paying Out Big This Month',
      excerpt: 'We\'ve tracked the highest-paying slots across UK casinos this month. These titles are delivering impressive RTP rates and frequent big wins to players.',
      date: '28.11.2025'
    },
    {
      id: 3,
      slug: 'live-dealer-games-future',
      title: 'Live Dealer Games: The Future of Online Casino Entertainment',
      excerpt: 'Live dealer technology continues to evolve, bringing authentic casino floor experiences directly to your screen. Discover which operators are leading the innovation.',
      date: '25.11.2025'
    },
    {
      id: 4,
      slug: 'cryptocurrency-payments-uk-casinos',
      title: 'Cryptocurrency Payments: Are UK Casinos Ready?',
      excerpt: 'As digital currencies gain mainstream acceptance, UK casinos are exploring crypto payment options. We examine which operators are embracing blockchain technology and what it means for players.',
      date: '22.11.2025'
    },
    {
      id: 5,
      slug: 'mobile-apps-vs-browser-play',
      title: 'Mobile Casino Apps vs Browser Play: Which is Better?',
      excerpt: 'The debate between dedicated mobile apps and browser-based play continues. We break down the pros and cons of each approach and reveal which offers the superior experience for UK players.',
      date: '20.11.2025'
    },
    {
      id: 6,
      slug: 'vip-programs-explained',
      title: 'VIP Programs Explained: Are They Worth It?',
      excerpt: 'Casino VIP programs promise exclusive perks and personalized service, but do they deliver real value? We analyze what it takes to reach VIP status and whether the benefits justify the requirements.',
      date: '18.11.2025'
    },
    {
      id: 7,
      slug: 'new-game-releases-season',
      title: 'New Game Releases: What\'s Hot This Season',
      excerpt: 'Game developers are pushing boundaries with innovative features and engaging themes. Here are the latest releases making waves across UK casinos and why players are flocking to them.',
      date: '15.11.2025'
    },
    {
      id: 8,
      slug: 'withdrawal-speed-comparison',
      title: 'Withdrawal Speed Comparison: Which Casinos Pay Fastest?',
      excerpt: 'Waiting days for withdrawals kills the excitement of winning. We\'ve tested withdrawal times across major UK operators to identify which casinos process payouts fastest and which keep you waiting.',
      date: '12.11.2025'
    },
    {
      id: 9,
      slug: 'responsible-gambling-tools-effectiveness',
      title: 'Responsible Gambling Tools: How Effective Are They?',
      excerpt: 'Deposit limits, reality checks, and self-exclusion tools are mandatory, but do they actually help players maintain control? We examine the effectiveness of responsible gambling measures and how to use them properly.',
      date: '10.11.2025'
    }
  ];

  return (
    <main className="min-h-screen py-20 bg-dark-black">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              UK Online Casino News
            </h1>
            <p className="text-gray-400 text-xl max-w-3xl mx-auto">
              Stay informed about the latest developments, trends, and insights shaping the UK online casino industry.
            </p>
          </div>

          {/* News Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {newsItems.map((item, index) => (
              <article
                key={item.id}
                itemScope
                itemType="https://schema.org/NewsArticle"
                className={`bg-gradient-to-br ${
                  index === 0
                    ? 'from-toxic-orange/20 to-dark-black border-2 border-toxic-orange/30'
                    : 'from-gray-900 to-dark-black border border-gray-800'
                } rounded-xl p-6 hover:border-toxic-orange transition-all duration-300 transform hover:-translate-y-1 group`}
              >
                <Link
                  to={`/news/${item.slug}`}
                  data-discover="true"
                  className="block"
                >
                  <div className="w-full h-48 bg-gray-800 rounded-lg mb-4 overflow-hidden border border-gray-700">
                    <img 
                      src={`/images/${index === 0 ? 'milos-tomasevic-f0QATZfOTv8-unsplash.webp' : index === 1 ? 'nemo-Fu4cSc9Gsp0-unsplash.webp' : index === 2 ? 'paige-laine-elmer-2zTtKmIDUXc-unsplash.webp' : index === 3 ? 'chris-liverani-vBpd607jLXs-unsplash.webp' : index === 4 ? 'documerica-mC_g8eqXV9U-unsplash.webp' : index === 5 ? 'erik-mclean-uraAsBooxOo-unsplash.webp' : index === 6 ? 'carl-raw-fzJPPfBhZ38-unsplash (1).webp' : index === 7 ? 'aceofnet-9zfx7an9r-8-unsplash.webp' : 'kenny-eliason-VOHZuZ8U4Lg-unsplash.webp'}`} 
                      alt={item.title} 
                      className="w-full h-full object-cover"
                      itemProp="image"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                    <Calendar size={16} className="text-toxic-orange" />
                    <time dateTime={item.date.split('.').reverse().join('-')} itemProp="datePublished">{item.date}</time>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3 leading-tight group-hover:text-toxic-orange transition-colors" itemProp="headline">
                    {item.title}
                  </h3>
                  
                  <p className="text-gray-300 text-sm leading-relaxed mb-4" itemProp="description">
                    {item.excerpt}
                  </p>
                </Link>
                
                <Link
                  to={`/news/${item.slug}`}
                  data-discover="true"
                  className="inline-flex items-center text-toxic-orange font-semibold group-hover:text-orange-400 transition-colors text-sm"
                >
                  Read More
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </Link>
              </article>
            ))}
          </div>

          {/* How We Choose News Section */}
          <div className="bg-gradient-to-br from-gray-900 to-dark-black border border-gray-800 rounded-2xl p-8 md:p-12 mb-16">
            <div className="flex items-start gap-6 mb-8">
              <div className="bg-toxic-orange/20 p-4 rounded-lg">
                <Newspaper className="text-toxic-orange" size={40} />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  How We Choose Our News
                </h2>
              </div>
            </div>
            
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                The UK online casino industry moves fast. New regulations emerge. Game developers release innovative titles. Operators launch promotions. Payment methods evolve. With so much happening, deciding what's actually newsworthy versus what's just marketing noise requires careful judgment. Here's how we filter the signal from the static.
              </p>
              
              <p>
                First, we focus on developments that genuinely impact players. A new slot release isn't news unless it introduces something meaningful—revolutionary features, exceptional RTP, or industry-shifting mechanics. A casino launching another generic welcome bonus isn't news. A casino introducing instant withdrawals or groundbreaking responsible gambling tools is. We prioritize stories that affect your experience, your bankroll, or your ability to play safely and fairly.
              </p>
              
              <p>
                Regulatory changes always make the cut because they directly affect how casinos operate and how you can play. When the UK Gambling Commission introduces new rules, we break down what they mean in practical terms. Not legal jargon—real implications. Will this slow down withdrawals? Does this change bonus terms? How does this protect players? We translate complex regulations into actionable information you can actually use.
              </p>
              
              <p>
                We track trends that matter, not fleeting fads. Cryptocurrency adoption in UK casinos is worth covering because it affects payment options and potentially withdrawal speeds. A single casino adding Bitcoin isn't news—multiple operators embracing crypto and making it work smoothly is. We look for patterns that indicate industry shifts, not one-off experiments that might disappear next month.
              </p>
              
              <p>
                Our news prioritizes accuracy over speed. We'd rather publish a thorough, verified story a day later than rush out incomplete information that misleads readers. Every claim we make is fact-checked. Every statistic is verified. Every operator mentioned is researched. We don't publish press releases verbatim—we investigate, analyze, and provide context that helps you understand what's really happening.
              </p>
              
              <p>
                We avoid sensationalism. Headlines like "Casino X Pays Out Million Pound Jackpot" might generate clicks, but they don't help you make better decisions. We focus on stories that provide value: which casinos process withdrawals fastest, which games offer the best RTP, which operators implement the most effective responsible gambling tools. News that helps you play smarter, not just stories that generate excitement.
              </p>
              
              <p>
                Player impact drives our editorial decisions. A new game might look impressive, but if it has terrible RTP or confusing mechanics, we'll explain why it's not worth your time. A casino might launch a massive welcome bonus, but if the wagering requirements make it nearly impossible to clear, we'll break down the math and show you why it's not as valuable as it appears. We're on your side, not the industry's.
              </p>
              
              <p>
                We cover both positive and negative developments. When casinos improve their services—faster withdrawals, better customer support, fairer bonus terms—we highlight those improvements. When casinos implement problematic policies or face regulatory action, we cover that too. Balanced reporting helps you make informed decisions rather than just seeing marketing-friendly stories.
              </p>
              
              <p>
                Our news focuses on UK-specific developments. International casino news might be interesting, but if it doesn't affect UK players, it's not our priority. We cover UK Gambling Commission actions, UK-licensed operators, games available to UK players, and payment methods that work in the UK. This focus ensures every story we publish is relevant to your experience.
              </p>
              
              <p>
                We verify everything independently. If a casino claims to have the fastest withdrawals, we test it. If a game developer claims revolutionary features, we play the game and assess whether the claims hold up. If regulators announce new rules, we read the actual regulations, not just summaries. Independent verification prevents us from becoming a mouthpiece for industry marketing.
              </p>
              
              <p>
                Finally, we prioritize stories that help you play better. News about withdrawal speeds helps you choose casinos that pay out faster. News about new regulations helps you understand your rights and protections. News about game releases helps you discover titles worth playing. Every story should provide actionable value, not just information for information's sake.
              </p>
              
              <p>
                The UK online casino industry is complex, fast-moving, and full of marketing noise. Our job is to cut through that noise and deliver news that matters—stories that help you make smarter decisions, understand industry developments, and get maximum value from your casino experience. We don't chase clicks or publish fluff. We focus on substance, accuracy, and player value. That's how we choose our news.
              </p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default News;
